import { action, atom, noop } from '@reatom/framework'
import { match } from 'ts-pattern'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { Ether } from '@root/entities/currency'
import {
  SuggestedGasSettings,
  suggestedGasSettingsPollingReatom,
} from '@root/entities/gas-information'
import { nativePriceAtom } from '@root/features/realtime/atoms'

import type { Error } from '@root/shared'

export type GasSettingsSelection =
  | {
      type: 'Recommended'
      variant: SuggestedGasSettings.SettingVariant
      settings: AsyncStateN.AsyncStateN<
        Error.UnknownError,
        SuggestedGasSettings.SuggestedSettings
      >
    }
  | {
      type: 'Advanced'
      settings: SuggestedGasSettings.SuggestedSettings
    }

export type GasSettingsVariant =
  | SuggestedGasSettings.SettingVariant
  | 'advanced'

export const transferGasSettingsReatom = () => {
  const gasSettingsVariantAtom = atom<GasSettingsVariant>(
    'market',
    'gasSettingsVariantAtom',
  )

  const advancedSettingsAtom = atom<
    SuggestedGasSettings.SuggestedSettings | undefined
  >(undefined, 'advancedSettingsAtom')

  const { suggestedSettingsStateAtom } = suggestedGasSettingsPollingReatom()

  const disposeTransferFeeAction = action(noop, 'disposeTransferFeeAction')

  const gasSettingsSelectionAtom = atom((ctx): GasSettingsSelection => {
    const variant = ctx.spy(gasSettingsVariantAtom)
    const gasState = ctx.spy(suggestedSettingsStateAtom)

    if (variant === 'advanced') {
      const advancedSettings = ctx.spy(advancedSettingsAtom)
      if (!advancedSettings) {
        return {
          type: 'Recommended',
          variant: 'market',
          settings: pipe(
            gasState,
            AsyncStateN.map((it) => it.variants.market),
          ),
        }
      }

      return {
        type: 'Advanced',
        settings: advancedSettings,
      }
    }

    const settingsState = pipe(
      gasState,
      AsyncStateN.map((it) => it.variants[variant]),
    )

    return {
      type: 'Recommended',
      variant: variant,
      settings: settingsState,
    }
  }, 'gasSettingsSelectionAtom')

  const selectedGasSettingsStateAtom = atom((ctx) => {
    const selection = ctx.spy(gasSettingsSelectionAtom)
    if (selection.type === 'Recommended') {
      return selection.settings
    }
    return AsyncStateN.success(selection.settings)
  }, 'selectedGasSettingsStateAtom')

  const setGasSettingsVariantAction = action(
    (ctx, variant: GasSettingsVariant) => {
      gasSettingsVariantAtom(ctx, variant)
    },
    'setGasSettingsVariantAction',
  )

  const setAdvancedSettingsAction = action(
    (ctx, variant: SuggestedGasSettings.SuggestedSettings) => {
      advancedSettingsAtom(ctx, variant)
      setGasSettingsVariantAction(ctx, 'advanced')
    },
    'setAdvancedSettingsAction',
  )

  const marketSettingsAtom = atom((ctx) => {
    const state = ctx.spy(suggestedSettingsStateAtom)
    return state.value?.variants.market
  }, 'marketSettingsAtom')

  const estimatedGasFeePerGasGweiAtom = atom((ctx) => {
    const settings = ctx.spy(gasSettingsSelectionAtom)
    const value = match(settings)
      .with({ type: 'Advanced' }, (it) => it.settings.suggestedMaxFeePerGas)
      .with(
        { type: 'Recommended' },
        (it) => it.settings.value?.suggestedMaxFeePerGas,
      )
      .exhaustive()

    return value
  }, 'estimatedGasFeePerGasGweiAtom')

  const estimatedGasFeeUsdAtom = atom((ctx) => {
    const gwei = ctx.spy(estimatedGasFeePerGasGweiAtom)

    if (gwei === undefined) {
      return undefined
    }

    const nativePrice = ctx.spy(nativePriceAtom)
    if (nativePrice.isZero()) {
      return undefined
    }
    const eth = Ether.fromGwei(gwei)
    return eth.multipliedBy(nativePrice)
  }, 'estimatedGasFeeUsdAtom')

  const estimatedTransferGasFeeGweiStateAtom = atom((ctx) => {
    const gasInformationState = ctx.get(suggestedSettingsStateAtom)
    const settingsState = ctx.spy(selectedGasSettingsStateAtom)
    return pipe(
      AsyncStateN.combineI(
        [gasInformationState, settingsState],
        (gasInfo, settings) => {
          const gwei = SuggestedGasSettings.calculateTransferEstimatedFeeInGwei(
            settings,
            gasInfo.estimatedBaseFeeGwei,
          )

          return gwei
        },
      ),
    )
  }, 'estimatedTransferGasFeeGweiStateAtom')

  const estimatedTransferMaxGasFeeGweiStateAtom = atom((ctx) => {
    const settingsState = ctx.spy(selectedGasSettingsStateAtom)

    return pipe(
      settingsState,
      AsyncStateN.map((settings) => {
        return SuggestedGasSettings.calculateTransferMaxFeeInGwei(settings)
      }),
    )
  }, 'estimatedTransferMaxGasFeeGweiStateAtom')

  const estimatedTransferMaxGasFeeEthStateAtom = atom((ctx) => {
    return pipe(
      ctx.spy(estimatedTransferMaxGasFeeGweiStateAtom),
      AsyncStateN.map((it) => Ether.fromGwei(it)),
    )
  }, 'estimatedTransferMaxGasFeeEthStateAtom')

  const estimatedTransferGasFeeEthStateAtom = atom((ctx) => {
    const state = ctx.spy(estimatedTransferGasFeeGweiStateAtom)
    return pipe(
      state,
      AsyncStateN.map((it) => Ether.fromGwei(it)),
    )
  }, 'estimatedTransferGasFeeEthStateAtom')

  const estimatedTransferGasFeeUsdStateAtom = atom((ctx) => {
    const feeState = ctx.spy(estimatedTransferGasFeeEthStateAtom)
    const nativePriceState = ctx.spy(nativePriceAtom)

    const feeInUsd = pipe(
      feeState,
      AsyncStateN.map((it) => it.multipliedBy(nativePriceState)),
    )

    return feeInUsd
  }, 'estimatedTransferGasFeeUsdStateAtom')

  return {
    gasInformationStateAtom: suggestedSettingsStateAtom,
    disposeTransferFeeAction,
    gasSettingsVariantAtom,
    selectedGasSettings: gasSettingsSelectionAtom,
    advancedSettingsAtom,
    setGasSettingsVariantAction,
    marketSettingsAtom,
    setAdvancedSettingsAction,
    estimatedGasFeeUsdAtom,
    estimatedTransferGasFeeEthStateAtom,
    estimatedTransferGasFeeUsdStateAtom,
    estimatedTransferMaxGasFeeEthStateAtom,
  }
}

export type GasProperties = {}

export type TransferGasSettingsReatom = ReturnType<
  typeof transferGasSettingsReatom
>
